import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Wand2, Lightbulb, Copy, Trash2, Download, ShieldAlert, ClipboardList, CheckCircle2, PlayCircle, RotateCcw } from "lucide-react";
import mermaid from "mermaid";
import { AccessDenied } from "@/components/AccessDenied";
import { SchemataNotificationsBell } from "@/components/admin/SchemataNotificationsBell";
import { MermaidDiff } from "@/components/admin/MermaidDiff";


import {
  generateSchema,
  suggestSchemaImprovements,
  analyzeSystemImpact,
  generateImplementationPlan,
  simulateDryRun,
  generateRollbackPlan,
} from "@/lib/admin-schemata.functions";

import {
  downloadMermaidSvg,
  downloadMermaidPng,
  downloadMermaidPdf,
  downloadSchemaReportPdf,
} from "@/lib/mermaid-export";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Route = createFileRoute("/admin/schemata")({
  component: SchemataPage,
});

mermaid.initialize({
  startOnLoad: false,
  theme: "default",
  securityLevel: "loose",
  flowchart: { htmlLabels: true, curve: "basis" },
});

function MermaidView({ code }: { code: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setErr(null);
    if (!ref.current || !code.trim()) return;
    const id = `m-${Math.random().toString(36).slice(2, 10)}`;
    mermaid
      .render(id, code)
      .then(({ svg }) => {
        if (!cancelled && ref.current) ref.current.innerHTML = svg;
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [code]);
  if (err)
    return (
      <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
        <div className="font-medium text-destructive">Diagram se nepodařilo vykreslit</div>
        <pre className="mt-2 whitespace-pre-wrap text-xs">{err}</pre>
      </div>
    );
  return <div ref={ref} className="overflow-auto rounded-md border bg-background p-4" />;
}

type Stage = "draft" | "design_approved" | "impact_approved" | "ready";

type Schema = {
  id: string;
  prompt: string;
  mermaid: string;
  mermaidPrevious?: string | null;
  suggestions?: string | null;
  stage: Stage;
  impactAnalysis?: string | null;
  impactMermaid?: string | null;
  impactMermaidPrevious?: string | null;
  implementationPlan?: string | null;
  dryRunResult?: string | null;
  dryRunAt?: number | null;
  rollbackPlan?: string | null;
  rollbackPlanAt?: number | null;
  createdAt: number;
};

type DbRow = {
  id: string;
  prompt: string;
  mermaid: string;
  mermaid_previous: string | null;
  suggestions: string | null;
  stage: string | null;
  impact_analysis: string | null;
  impact_mermaid: string | null;
  impact_mermaid_previous: string | null;
  implementation_plan: string | null;
  dry_run_result: string | null;
  dry_run_at: string | null;
  rollback_plan: string | null;
  rollback_plan_at: string | null;
  created_at: string;
};

type AuditAction = "generate" | "suggest" | "impact" | "plan" | "dry_run" | "rollback";

type AuditRow = {
  id: string;
  user_id: string;
  schema_id: string | null;
  action: AuditAction;
  prompt: string;
  module_name: string | null;
  scope_items: string[] | null;
  created_at: string;
};

type UserMeta = { email: string | null; fullName: string | null; roles: string[] };

const ACTION_LABELS: Record<AuditAction, string> = {
  generate: "Vygenerovat diagram",
  suggest: "Navrhnout vylepšení",
  impact: "Schválit dopady / analýza",
  plan: "Implementační plán",
  dry_run: "Dry-run simulace",
  rollback: "Rollback plán",
};

const SCHEMATA_ROLES = new Set([
  "admin",
  "schemata_proposer",
  "schemata_approver",
  "schemata_implementer",
]);


function rowToSchema(r: DbRow): Schema {
  return {
    id: r.id,
    prompt: r.prompt,
    mermaid: r.mermaid,
    mermaidPrevious: r.mermaid_previous,
    suggestions: r.suggestions,
    stage: ((r.stage as Stage) ?? "draft") as Stage,
    impactAnalysis: r.impact_analysis,
    impactMermaid: r.impact_mermaid,
    impactMermaidPrevious: r.impact_mermaid_previous,
    implementationPlan: r.implementation_plan,
    dryRunResult: r.dry_run_result,
    dryRunAt: r.dry_run_at ? new Date(r.dry_run_at).getTime() : null,
    rollbackPlan: r.rollback_plan,
    rollbackPlanAt: r.rollback_plan_at ? new Date(r.rollback_plan_at).getTime() : null,
    createdAt: new Date(r.created_at).getTime(),
  };
}



function SchemataPage() {
  const { hasRole, loading, user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = hasRole("admin");
  const isProposer = isAdmin || hasRole("schemata_proposer") || hasRole("schemata_approver");
  const isApprover = isAdmin || hasRole("schemata_approver");
  const isImplementer = isAdmin || hasRole("schemata_implementer") || hasRole("schemata_approver");
  const hasAccess = isAdmin || isProposer || isApprover || isImplementer;

  useEffect(() => {
    if (!loading && (!user || !hasAccess)) {
      navigate({ to: "/admin" });
    }
  }, [loading, user, hasAccess, navigate]);

  const generate = useServerFn(generateSchema);
  const suggest = useServerFn(suggestSchemaImprovements);
  const analyzeImpact = useServerFn(analyzeSystemImpact);
  const generatePlan = useServerFn(generateImplementationPlan);
  const runDryRun = useServerFn(simulateDryRun);
  const runRollback = useServerFn(generateRollbackPlan);

  const [prompt, setPrompt] = useState("");
  const [moduleName, setModuleName] = useState("");
  const [scopeText, setScopeText] = useState("");
  const [busy, setBusy] = useState<null | "gen" | "sug" | "impact" | "plan" | "dry" | "rb">(null);
  const [items, setItems] = useState<Schema[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [auditRows, setAuditRows] = useState<AuditRow[]>([]);
  const [auditReloadKey, setAuditReloadKey] = useState(0);
  const [userMeta, setUserMeta] = useState<Record<string, UserMeta>>({});
  const [filterUser, setFilterUser] = useState<string>("all");
  const [filterAction, setFilterAction] = useState<"all" | AuditAction>("all");
  const [filterText, setFilterText] = useState("");

  const active = items.find((s) => s.id === activeId) ?? null;

  const SELECT_COLS =
    "id, prompt, mermaid, mermaid_previous, suggestions, stage, impact_analysis, impact_mermaid, impact_mermaid_previous, implementation_plan, dry_run_result, dry_run_at, rollback_plan, rollback_plan_at, created_at";


  // Load persisted history on mount (admin only — RLS enforces).
  useEffect(() => {
    if (!hasAccess || !user) return;
    let cancelled = false;
    setLoadingHistory(true);
    supabase
      .from("admin_schemata")
      .select(SELECT_COLS)

      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          toast.error("Historii se nepodařilo načíst.");
        } else if (data) {
          const mapped = (data as DbRow[]).map(rowToSchema);
          setItems(mapped);
          if (mapped[0]) setActiveId(mapped[0].id);
        }
        setLoadingHistory(false);
      });
    return () => { cancelled = true; };
  }, [hasAccess, user?.id]);

  // Audit log — jen pro admina (RLS filtruje).
  useEffect(() => {
    if (!hasAccess || !user) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("admin_schemata_audit")
        .select("id, user_id, schema_id, action, prompt, module_name, scope_items, created_at")
        .order("created_at", { ascending: false })
        .limit(500);
      if (cancelled) return;
      if (error) return;
      const rows = (data ?? []) as AuditRow[];
      setAuditRows(rows);

      const uniqueIds = Array.from(new Set(rows.map((r) => r.user_id))).filter(Boolean);
      if (uniqueIds.length === 0) {
        setUserMeta({});
        return;
      }
      const [profilesRes, rolesRes] = await Promise.all([
        supabase.from("profiles").select("id, email, full_name").in("id", uniqueIds),
        supabase.from("user_roles").select("user_id, role").in("user_id", uniqueIds),
      ]);
      if (cancelled) return;
      const meta: Record<string, UserMeta> = {};
      for (const id of uniqueIds) meta[id] = { email: null, fullName: null, roles: [] };
      for (const p of (profilesRes.data ?? []) as Array<{ id: string; email: string | null; full_name: string | null }>) {
        if (meta[p.id]) {
          meta[p.id].email = p.email;
          meta[p.id].fullName = p.full_name;
        }
      }
      for (const r of (rolesRes.data ?? []) as Array<{ user_id: string; role: string }>) {
        if (meta[r.user_id] && SCHEMATA_ROLES.has(r.role)) {
          meta[r.user_id].roles.push(r.role);
        }
      }
      setUserMeta(meta);
    })();
    return () => { cancelled = true; };

  }, [hasAccess, user?.id, auditReloadKey]);

  const onGenerate = async () => {
    if (!prompt.trim()) return;
    setBusy("gen");
    try {
      const scopeItems = scopeText
        .split(/[\n,;]/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .slice(0, 50);
      const mod = moduleName.trim();
      const r = await generate({
        data: {
          prompt,
          ...(mod ? { moduleName: mod } : {}),
          ...(scopeItems.length ? { scopeItems } : {}),
        },
      });
      const promptForRecord = [
        prompt.trim(),
        mod ? `[modul: ${mod}]` : "",
        scopeItems.length ? `[rozsah: ${scopeItems.join(", ")}]` : "",
      ]
        .filter(Boolean)
        .join(" ");
      const { data, error } = await supabase
        .from("admin_schemata")
        .insert({
          user_id: user!.id,
          prompt: promptForRecord,
          mermaid: r.mermaid,
        })
        .select(SELECT_COLS)
        .single();
      if (error || !data) throw new Error(error?.message ?? "Uložení selhalo.");
      const s = rowToSchema(data as DbRow);
      setItems((prev) => [s, ...prev]);
      setActiveId(s.id);
      setPrompt("");
      setModuleName("");
      setScopeText("");
      toast.success("Diagram vygenerován a uložen.");
      setAuditReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Generování selhalo.");
    } finally {
      setBusy(null);
    }
  };

  const onSuggest = async () => {
    if (!active) return;
    setBusy("sug");
    try {
      const r = await suggest({
        data: { prompt: active.prompt, currentMermaid: active.mermaid, schemaId: active.id },
      });
      const previousMermaid = active.mermaid;
      const { error } = await supabase
        .from("admin_schemata")
        .update({
          mermaid: r.mermaid,
          mermaid_previous: previousMermaid,
          suggestions: r.suggestions,
        })
        .eq("id", active.id);
      if (error) throw new Error(error.message);
      setItems((prev) =>
        prev.map((s) =>
          s.id === active.id
            ? {
                ...s,
                mermaid: r.mermaid,
                mermaidPrevious: previousMermaid,
                suggestions: r.suggestions,
              }
            : s,
        ),
      );

      toast.success("Návrhy vylepšení připraveny a uloženy.");
      setAuditReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Návrhy selhaly.");
    } finally {
      setBusy(null);
    }
  };

  const patchActive = (patch: Partial<Schema>) => {
    setItems((prev) => prev.map((s) => (s.id === activeId ? { ...s, ...patch } : s)));
  };

  const onApproveDesign = async () => {
    if (!active) return;
    setBusy("impact");
    try {
      const r = await analyzeImpact({
        data: {
          prompt: active.prompt,
          currentMermaid: active.mermaid,
          suggestions: active.suggestions ?? undefined,
          schemaId: active.id,
        },
      });
      const nowIso = new Date().toISOString();
      const previousImpact = active.impactMermaid ?? null;
      const { error } = await supabase
        .from("admin_schemata")
        .update({
          stage: "design_approved",
          impact_analysis: r.analysis,
          impact_mermaid: r.impactMermaid,
          impact_mermaid_previous: previousImpact,
          approved_design_at: nowIso,
        })
        .eq("id", active.id);
      if (error) throw new Error(error.message);
      patchActive({
        stage: "design_approved",
        impactAnalysis: r.analysis,
        impactMermaid: r.impactMermaid,
        impactMermaidPrevious: previousImpact,
      });

      toast.success("Návrh schválen, analýza dopadů připravena.");
      setAuditReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Analýza dopadů selhala.");
    } finally {
      setBusy(null);
    }
  };

  const onApproveImpact = async () => {
    if (!active || !active.impactAnalysis) return;
    setBusy("plan");
    try {
      const r = await generatePlan({
        data: {
          prompt: active.prompt,
          currentMermaid: active.mermaid,
          suggestions: active.suggestions ?? undefined,
          impactAnalysis: active.impactAnalysis,
          schemaId: active.id,
        },
      });
      const nowIso = new Date().toISOString();
      const { error } = await supabase
        .from("admin_schemata")
        .update({
          stage: "impact_approved",
          implementation_plan: r.plan,
          approved_impact_at: nowIso,
        })
        .eq("id", active.id);
      if (error) throw new Error(error.message);
      patchActive({ stage: "impact_approved", implementationPlan: r.plan });
      toast.success("Dopady schváleny, implementační plán připraven.");
      setAuditReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Plán se nepodařilo vygenerovat.");
    } finally {
      setBusy(null);
    }
  };

  const onMarkReady = async () => {
    if (!active) return;
    const { error } = await supabase
      .from("admin_schemata")
      .update({ stage: "ready" })
      .eq("id", active.id);
    if (error) {
      toast.error("Nepodařilo se označit jako připravené.");
      return;
    }
    patchActive({ stage: "ready" });
    toast.success("Plán schválen — připraveno k implementaci.");
  };

  const onDryRun = async () => {
    if (!active) return;
    setBusy("dry");
    try {
      const r = await runDryRun({
        data: {
          prompt: active.prompt,
          currentMermaid: active.mermaid,
          suggestions: active.suggestions ?? undefined,
          impactAnalysis: active.impactAnalysis ?? undefined,
          implementationPlan: active.implementationPlan ?? undefined,
          schemaId: active.id,
        },
      });
      const nowIso = new Date().toISOString();
      const { error } = await supabase
        .from("admin_schemata")
        .update({ dry_run_result: r.dryRun, dry_run_at: nowIso })
        .eq("id", active.id);
      if (error) throw new Error(error.message);
      patchActive({ dryRunResult: r.dryRun, dryRunAt: new Date(nowIso).getTime() });
      toast.success("Simulace dopadů dokončena.");
      setAuditReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Simulace selhala.");
    } finally {
      setBusy(null);
    }
  };

  const onGenerateRollback = async () => {
    if (!active) return;
    setBusy("rb");
    try {
      const r = await runRollback({
        data: {
          prompt: active.prompt,
          currentMermaid: active.mermaid,
          suggestions: active.suggestions ?? undefined,
          impactAnalysis: active.impactAnalysis ?? undefined,
          implementationPlan: active.implementationPlan ?? undefined,
          dryRunResult: active.dryRunResult ?? undefined,
          schemaId: active.id,
        },
      });
      const nowIso = new Date().toISOString();
      const { error } = await supabase
        .from("admin_schemata")
        .update({ rollback_plan: r.rollbackPlan, rollback_plan_at: nowIso })
        .eq("id", active.id);
      if (error) throw new Error(error.message);
      patchActive({ rollbackPlan: r.rollbackPlan, rollbackPlanAt: new Date(nowIso).getTime() });
      toast.success("Rollback plán vygenerován.");
      setAuditReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Generování rollback plánu selhalo.");
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async (id: string) => {
    const { error } = await supabase.from("admin_schemata").delete().eq("id", id);
    if (error) {
      toast.error("Smazání selhalo.");
      return;
    }
    setItems((prev) => prev.filter((s) => s.id !== id));
    setActiveId((cur) => (cur === id ? null : cur));
    toast.success("Diagram smazán.");
  };


  if (loading) {
    return <div className="text-sm text-muted-foreground">Načítám…</div>;
  }
  if (!hasAccess) {
    return (
      <AccessDenied
        title="Přístup odepřen"
        description="Sekce Schémata je dostupná pouze uživatelům s rolí schemata_proposer, schemata_approver, schemata_implementer nebo admin."
        requiredRole="admin"
      />
    );
  }

  const roleBadges: { label: string; active: boolean; tone: string }[] = [
    { label: "admin", active: isAdmin, tone: "bg-primary text-primary-foreground" },
    { label: "navrhovatel", active: hasRole("schemata_proposer") || isAdmin, tone: "bg-blue-600 text-white" },
    { label: "schvalovatel", active: hasRole("schemata_approver") || isAdmin, tone: "bg-amber-600 text-white" },
    { label: "implementátor", active: hasRole("schemata_implementer") || isAdmin, tone: "bg-emerald-600 text-white" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h1 className="font-serif text-3xl">Schémata</h1>
          <SchemataNotificationsBell />
        </div>
        <p className="text-sm text-muted-foreground">
          Vývojové diagramy funkcí systému. Popiš funkci a nech vygenerovat Mermaid diagram;
          následně můžeš vyžádat návrhy vylepšení, které se rovnou promítnou do diagramu.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Tvoje role ve workflow:</span>
          {roleBadges.filter((r) => r.active).map((r) => (
            <span key={r.label} className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${r.tone}`}>
              {r.label}
            </span>
          ))}
          {roleBadges.every((r) => !r.active) && (
            <span className="text-xs italic text-muted-foreground">bez role</span>
          )}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Navrhovatel vytváří návrhy a diagramy · schvalovatel potvrzuje jednotlivé fáze workflow ·
          implementátor spouští simulaci a rollback plán · admin má plný přístup.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-xl">Nový diagram</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder='Popiš funkci, kterou chceš znázornit (např. „proces schvalování podniku od odeslání po publikaci").'
            rows={4}
            disabled={busy !== null}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                Modul / soubor (volitelné)
              </label>
              <input
                type="text"
                value={moduleName}
                onChange={(e) => setModuleName(e.target.value)}
                placeholder="např. src/routes/mapa.tsx nebo modul „Katalog import"
                disabled={busy !== null}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                Konkrétní funkce / procesy (volitelné, oddělené čárkou nebo novým řádkem)
              </label>
              <Textarea
                value={scopeText}
                onChange={(e) => setScopeText(e.target.value)}
                placeholder={"např.\nvalidateImport\napplyBatch\nnotifyReviewers"}
                rows={3}
                disabled={busy !== null}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Pokud vyplníš modul nebo výčet funkcí, AI bude diagramovat pouze tento rozsah a nic okolo.
          </p>
          <div className="flex flex-col items-end gap-1">
            <Button
              onClick={onGenerate}
              disabled={busy !== null || !prompt.trim() || !isProposer}
              title={!isProposer ? "Vyžadovaná role: schemata_proposer nebo schemata_approver" : undefined}
            >
              {busy === "gen" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="mr-2 h-4 w-4" />
              )}
              Vygenerovat diagram
            </Button>
            {!isProposer && (
              <span className="text-[11px] text-muted-foreground">
                Pouze pro roli navrhovatel / schvalovatel / admin.
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {loadingHistory && items.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Načítám historii…
        </div>
      )}

      {items.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
          <Card>
            <CardHeader>
              <CardTitle className="font-serif text-base">Historie</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {items.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveId(s.id)}
                  className={`block w-full rounded-md border p-2 text-left text-sm transition-colors hover:bg-accent ${
                    s.id === activeId ? "border-primary bg-accent" : ""
                  }`}
                >
                  <div className="line-clamp-2 font-medium">{s.prompt}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">
                      {new Date(s.createdAt).toLocaleTimeString("cs-CZ")}
                    </Badge>
                    {s.suggestions && (
                      <Badge variant="secondary" className="text-[10px]">
                        vylepšeno
                      </Badge>
                    )}
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>

          <div className="space-y-4">
            {active && (
              <>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="font-serif text-xl">{active.prompt}</CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(active.mermaid);
                          toast.success("Mermaid zdroj zkopírován.");
                        }}
                      >
                        <Copy className="mr-1 h-3 w-3" /> Zdroj
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Download className="mr-1 h-3 w-3" /> Stáhnout
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() =>
                              downloadMermaidSvg(active.mermaid, active.prompt).catch((e) =>
                                toast.error(e instanceof Error ? e.message : "Export SVG selhal."),
                              )
                            }
                          >
                            SVG
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              downloadMermaidPng(active.mermaid, active.prompt).catch((e) =>
                                toast.error(e instanceof Error ? e.message : "Export PNG selhal."),
                              )
                            }
                          >
                            PNG
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              downloadMermaidPdf(active.mermaid, active.prompt).catch((e) =>
                                toast.error(e instanceof Error ? e.message : "Export PDF selhal."),
                              )
                            }
                          >
                            PDF (jen diagram)
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              downloadSchemaReportPdf({
                                title: active.prompt,
                                prompt: active.prompt,
                                stage: active.stage,
                                createdAt: active.createdAt,
                                mermaid: active.mermaid,
                                sections: [
                                  active.suggestions
                                    ? { title: "Návrhy vylepšení", body: active.suggestions }
                                    : null,
                                  active.impactMermaid
                                    ? { title: "Analýza dopadů — diagram", mermaid: active.impactMermaid }
                                    : null,
                                  active.impactAnalysis
                                    ? { title: "Analýza dopadů", body: active.impactAnalysis }
                                    : null,
                                  active.implementationPlan
                                    ? { title: "Plán implementace", body: active.implementationPlan }
                                    : null,
                                ].filter(Boolean) as { title: string; body?: string | null; mermaid?: string | null }[],
                              }).catch((e) =>
                                toast.error(e instanceof Error ? e.message : "Export reportu selhal."),
                              )
                            }
                          >
                            PDF report (souhrn)
                          </DropdownMenuItem>

                        </DropdownMenuContent>
                      </DropdownMenu>
                      {isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onDelete(active.id)}
                        >
                          <Trash2 className="mr-1 h-3 w-3" /> Smazat
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={onSuggest}
                        disabled={busy !== null || !isProposer}
                        title={!isProposer ? "Vyžadovaná role: schemata_proposer / schemata_approver / admin" : undefined}
                      >
                        {busy === "sug" ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Lightbulb className="mr-2 h-4 w-4" />
                        )}
                        Navrhnout vylepšení
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <MermaidView code={active.mermaid} />
                  </CardContent>
                </Card>

                {active.mermaidPrevious && active.mermaidPrevious !== active.mermaid && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="font-serif text-lg">
                        Diff diagramu — původní vs. navržený
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">
                        Změny navržené AI oproti předchozí verzi. Zkontroluj před schválením návrhu.
                      </p>
                    </CardHeader>
                    <CardContent>
                      <MermaidDiff
                        before={active.mermaidPrevious}
                        after={active.mermaid}
                        labelBefore="Původní diagram"
                        labelAfter="Navržený diagram"
                      />
                    </CardContent>
                  </Card>
                )}

                {active.suggestions && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="font-serif text-lg">Návrhy vylepšení</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <pre className="whitespace-pre-wrap text-sm">{active.suggestions}</pre>
                    </CardContent>
                  </Card>
                )}


                <Card>
                  <CardHeader className="flex flex-row items-center justify-between gap-2">
                    <div>
                      <CardTitle className="font-serif text-lg">Workflow schvalování</CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">
                        Fáze:{" "}
                        <Badge variant="outline">
                          {active.stage === "draft" && "1 / 4 — návrh"}
                          {active.stage === "design_approved" && "2 / 4 — analýza dopadů"}
                          {active.stage === "impact_approved" && "3 / 4 — implementační plán"}
                          {active.stage === "ready" && "4 / 4 — připraveno k implementaci"}
                        </Badge>
                      </p>
                    </div>
                    {active.stage === "draft" && (
                      <Button size="sm" onClick={onApproveDesign} disabled={busy !== null || !isApprover} title={!isApprover ? "Vyžadovaná role: schemata_approver / admin" : undefined}>
                        {busy === "impact" ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <ShieldAlert className="mr-2 h-4 w-4" />
                        )}
                        Schválit návrh & analyzovat dopady
                      </Button>
                    )}
                    {active.stage === "design_approved" && (
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={onApproveDesign} disabled={busy !== null || !isApprover} title={!isApprover ? "Vyžadovaná role: schemata_approver / admin" : undefined}>
                          <ShieldAlert className="mr-2 h-4 w-4" /> Přegenerovat analýzu
                        </Button>
                        <Button size="sm" onClick={onApproveImpact} disabled={busy !== null || !isApprover} title={!isApprover ? "Vyžadovaná role: schemata_approver / admin" : undefined}>
                          {busy === "plan" ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <ClipboardList className="mr-2 h-4 w-4" />
                          )}
                          Schválit dopady & připravit plán
                        </Button>
                      </div>
                    )}
                    {active.stage === "impact_approved" && (
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={onDryRun} disabled={busy !== null || !isImplementer} title={!isImplementer ? "Vyžadovaná role: schemata_implementer / schemata_approver / admin" : undefined}>
                          {busy === "dry" ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <PlayCircle className="mr-2 h-4 w-4" />
                          )}
                          Simulace dopadů (dry-run)
                        </Button>
                        <Button variant="outline" size="sm" onClick={onGenerateRollback} disabled={busy !== null || !isImplementer} title={!isImplementer ? "Vyžadovaná role: schemata_implementer / schemata_approver / admin" : undefined}>
                          {busy === "rb" ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <RotateCcw className="mr-2 h-4 w-4" />
                          )}
                          Rollback plán
                        </Button>
                        <Button size="sm" onClick={onMarkReady} disabled={!isApprover} title={!isApprover ? "Vyžadovaná role: schemata_approver / admin" : undefined}>
                          <CheckCircle2 className="mr-2 h-4 w-4" /> Schválit plán (připraveno k implementaci)
                        </Button>
                      </div>
                    )}
                    {active.stage === "ready" && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button variant="outline" size="sm" onClick={onDryRun} disabled={busy !== null || !isImplementer} title={!isImplementer ? "Vyžadovaná role: schemata_implementer / schemata_approver / admin" : undefined}>
                          {busy === "dry" ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <PlayCircle className="mr-2 h-4 w-4" />
                          )}
                          Znovu simulovat
                        </Button>
                        <Button variant="outline" size="sm" onClick={onGenerateRollback} disabled={busy !== null || !isImplementer} title={!isImplementer ? "Vyžadovaná role: schemata_implementer / schemata_approver / admin" : undefined}>
                          {busy === "rb" ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <RotateCcw className="mr-2 h-4 w-4" />
                          )}
                          {active.rollbackPlan ? "Přegenerovat rollback" : "Rollback plán"}
                        </Button>
                        <Badge className="bg-green-600 hover:bg-green-600">
                          <CheckCircle2 className="mr-1 h-3 w-3" /> Připraveno k implementaci
                        </Badge>
                      </div>
                    )}
                  </CardHeader>
                </Card>

                {active.impactAnalysis && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="font-serif text-lg">
                        Analýza dopadů & navrhované úpravy
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <pre className="whitespace-pre-wrap text-sm">{active.impactAnalysis}</pre>
                      {active.impactMermaid && (
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-muted-foreground">
                            Diagram problematických bodů
                          </div>
                          {active.impactMermaidPrevious &&
                          active.impactMermaidPrevious !== active.impactMermaid ? (
                            <MermaidDiff
                              before={active.impactMermaidPrevious}
                              after={active.impactMermaid}
                              labelBefore="Předchozí analýza"
                              labelAfter="Aktuální analýza"
                            />
                          ) : (
                            <MermaidView code={active.impactMermaid} />
                          )}
                        </div>
                      )}

                    </CardContent>
                  </Card>
                )}

                {active.implementationPlan && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="font-serif text-lg">Implementační plán</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <pre className="whitespace-pre-wrap text-sm">{active.implementationPlan}</pre>
                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(active.implementationPlan ?? "");
                            toast.success("Plán zkopírován do schránky.");
                          }}
                        >
                          <Copy className="mr-1 h-3 w-3" /> Zkopírovat plán
                        </Button>
                      </div>
                      {active.stage === "ready" && (
                        <p className="text-xs text-muted-foreground">
                          Plán je schválený. Předej ho Lovable („implementuj plán ze schématu #{active.id.slice(0, 8)}") nebo vývojáři k realizaci.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {active.dryRunResult && (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between gap-2">
                      <div>
                        <CardTitle className="font-serif text-lg flex items-center gap-2">
                          <PlayCircle className="h-4 w-4" /> Simulace dopadů (dry-run)
                        </CardTitle>
                        {active.dryRunAt && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Poslední simulace: {new Date(active.dryRunAt).toLocaleString("cs-CZ")}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Vyhodnocení bez zásahu do systému — použij pro rozhodnutí, jestli plán skutečně nasadit.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(active.dryRunResult ?? "");
                          toast.success("Výsledek simulace zkopírován.");
                        }}
                      >
                        <Copy className="mr-1 h-3 w-3" /> Zkopírovat
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <pre className="whitespace-pre-wrap text-sm">{active.dryRunResult}</pre>
                    </CardContent>
                  </Card>
                )}

                {active.rollbackPlan && (
                  <Card className="border-amber-300/60">
                    <CardHeader className="flex flex-row items-center justify-between gap-2">
                      <div>
                        <CardTitle className="font-serif text-lg flex items-center gap-2">
                          <RotateCcw className="h-4 w-4" /> Rollback plán
                        </CardTitle>
                        {active.rollbackPlanAt && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Vygenerováno: {new Date(active.rollbackPlanAt).toLocaleString("cs-CZ")}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Postup pro návrat systému do konzistentního stavu, pokud implementace selže nebo způsobí regresi.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(active.rollbackPlan ?? "");
                          toast.success("Rollback plán zkopírován.");
                        }}
                      >
                        <Copy className="mr-1 h-3 w-3" /> Zkopírovat
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <pre className="whitespace-pre-wrap text-sm">{active.rollbackPlan}</pre>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

          </div>
        </div>
      )}

      <AuditHistoryPanel
        rows={auditRows}
        userMeta={userMeta}
        currentUserId={user?.id ?? null}
        filterUser={filterUser}
        setFilterUser={setFilterUser}
        filterAction={filterAction}
        setFilterAction={setFilterAction}
        filterText={filterText}
        setFilterText={setFilterText}
        onReload={() => setAuditReloadKey((k) => k + 1)}
      />

    </div>
  );
}

function userLabel(id: string, meta: UserMeta | undefined, currentUserId: string | null): string {
  if (id === currentUserId) return "já";
  if (meta?.fullName) return meta.fullName;
  if (meta?.email) return meta.email;
  return id.slice(0, 8);
}

function AuditHistoryPanel({
  rows,
  userMeta,
  currentUserId,
  filterUser,
  setFilterUser,
  filterAction,
  setFilterAction,
  filterText,
  setFilterText,
  onReload,
}: {
  rows: AuditRow[];
  userMeta: Record<string, UserMeta>;
  currentUserId: string | null;
  filterUser: string;
  setFilterUser: (v: string) => void;
  filterAction: "all" | AuditAction;
  setFilterAction: (v: "all" | AuditAction) => void;
  filterText: string;
  setFilterText: (v: string) => void;
  onReload: () => void;
}) {
  const uniqueUsers = useMemo(() => {
    const ids = Array.from(new Set(rows.map((r) => r.user_id)));
    return ids
      .map((id) => ({ id, label: userLabel(id, userMeta[id], currentUserId) }))
      .sort((a, b) => a.label.localeCompare(b.label, "cs"));
  }, [rows, userMeta, currentUserId]);

  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    return rows.filter((r) => {
      if (filterUser !== "all" && r.user_id !== filterUser) return false;
      if (filterAction !== "all" && r.action !== filterAction) return false;
      if (q) {
        const hay = [
          r.prompt,
          r.module_name ?? "",
          (r.scope_items ?? []).join(" "),
          userMeta[r.user_id]?.email ?? "",
          userMeta[r.user_id]?.fullName ?? "",
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filterUser, filterAction, filterText, userMeta]);

  const reset = () => {
    setFilterUser("all");
    setFilterAction("all");
    setFilterText("");
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="font-serif text-xl">Auditní historie změn fází</CardTitle>
          <div className="text-xs text-muted-foreground">
            Zobrazeno {filtered.length} z {rows.length}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 md:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Uživatel</label>
            <Select value={filterUser} onValueChange={setFilterUser}>
              <SelectTrigger><SelectValue placeholder="Všichni" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všichni uživatelé</SelectItem>
                {uniqueUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Fáze / akce</label>
            <Select value={filterAction} onValueChange={(v) => setFilterAction(v as "all" | AuditAction)}>
              <SelectTrigger><SelectValue placeholder="Všechny fáze" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všechny fáze</SelectItem>
                {(Object.keys(ACTION_LABELS) as AuditAction[]).map((a) => (
                  <SelectItem key={a} value={a}>{ACTION_LABELS[a]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Hledat v promptu / rozsahu</label>
            <div className="flex gap-2">
              <Input
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder="fragment textu…"
              />
              <Button type="button" variant="outline" onClick={reset}>Reset</Button>
              <Button type="button" variant="ghost" onClick={onReload} title="Znovu načíst">↻</Button>
            </div>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            {rows.length === 0
              ? "Žádné akce zatím nebyly zaznamenány."
              : "Žádné záznamy neodpovídají zvoleným filtrům."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                  <th className="py-2 pr-3">Kdy</th>
                  <th className="py-2 pr-3">Fáze / akce</th>
                  <th className="py-2 pr-3">Uživatel</th>
                  <th className="py-2 pr-3">Role</th>
                  <th className="py-2 pr-3">Schválený obsah / prompt</th>
                  <th className="py-2 pr-3">Rozsah</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const meta = userMeta[r.user_id];
                  return (
                    <tr key={r.id} className="border-b align-top last:border-0">
                      <td className="py-2 pr-3 whitespace-nowrap text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString("cs-CZ")}
                      </td>
                      <td className="py-2 pr-3">
                        <Badge variant={r.action === "generate" ? "default" : "secondary"}>
                          {ACTION_LABELS[r.action]}
                        </Badge>
                        {r.schema_id && (
                          <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                            #{r.schema_id.slice(0, 8)}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-xs">
                        <div className="font-medium">{userLabel(r.user_id, meta, currentUserId)}</div>
                        {meta?.email && meta?.fullName && (
                          <div className="text-[11px] text-muted-foreground">{meta.email}</div>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex flex-wrap gap-1">
                          {(meta?.roles ?? []).length === 0 ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : (
                            meta!.roles.map((role) => (
                              <Badge
                                key={role}
                                variant={role === "admin" ? "default" : "outline"}
                                className="text-[10px]"
                              >
                                {role}
                              </Badge>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="line-clamp-3 max-w-md whitespace-pre-wrap">{r.prompt}</div>
                      </td>
                      <td className="py-2 pr-3 text-xs text-muted-foreground">
                        {r.module_name && <div>modul: {r.module_name}</div>}
                        {r.scope_items && r.scope_items.length > 0 && (
                          <div>fce: {r.scope_items.join(", ")}</div>
                        )}
                        {!r.module_name && !r.scope_items?.length && "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

